import { HIGH_RISK } from '../lib/risk';
import { Badge } from '../components/ui/badge';
import { CHANNELS, INSTALLMENT_STATUSES, SEQUENCE_STATUSES } from '@beta-assur/shared';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { InstallmentStatusBadge, SequenceStatusBadge } from '../components/badges';
import { PageHeader } from '../components/page';
import { Empty, ErrorState, Loading } from '../components/states';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Checkbox, Input, Label, Select } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { day, instant, money, parseDinars } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useCursorList } from '../lib/infinite';
import { useReferentials, useTenant } from '../lib/referentials';
import type { InstallmentRow } from '../lib/types';
import { useAuth } from '../lib/auth';
import { BulkBar } from './bulk-actions';

// Échéances (BA-022, spec 08 §2) : filtres dans l'URL (partageables), table paginée serveur.

/** Case à cocher dans l'adresse : `true` saisi à la main est lu comme booléen par le routeur. */
const urlFlag = z.preprocess((v) => (v === true ? 'true' : v), z.enum(['true'])).optional();

export const installmentSearchSchema = z.object({
  status: z.enum(INSTALLMENT_STATUSES).optional(),
  dueFrom: z.string().optional(),
  dueTo: z.string().optional(),
  minAmount: z.coerce.string().optional(),
  maxAmount: z.coerce.string().optional(),
  policyNumber: z.coerce.string().optional(),
  insurerId: z.string().optional(),
  productLineId: z.string().optional(),
  agentId: z.string().optional(),
  preferredChannel: z.enum(CHANNELS).optional(),
  sequenceStatus: z.enum(SEQUENCE_STATUSES).optional(),
  noPhone: urlFlag,
  /** Risque d'impayé élevé (BA-078). */
  highRisk: urlFlag,
});
export type InstallmentSearch = z.infer<typeof installmentSearchSchema>;

const clean = (search: Record<string, string | undefined>) =>
  Object.fromEntries(Object.entries(search).filter(([, v]) => v !== undefined && v !== ''));

function toApiQuery(search: InstallmentSearch) {
  const amount = (value: string | undefined) =>
    value ? (parseDinars(value) ?? undefined) : undefined;
  const { highRisk, ...rest } = search;
  return {
    ...rest,
    minAmount: amount(search.minAmount),
    maxAmount: amount(search.maxAmount),
    minRisk: highRisk === 'true' ? HIGH_RISK : undefined,
  };
}

export function InstallmentsPage() {
  const { t, language } = useI18n();
  const search = useSearch({ strict: false }) as InstallmentSearch;
  const navigate = useNavigate();
  const refs = useReferentials();
  const tz = useTenant().data?.timezone;
  const [draft, setDraft] = useState<Record<string, string | undefined>>(search);
  const list = useCursorList<InstallmentRow>('/v1/installments', toApiQuery(search));
  const auth = useAuth();
  const canBulk = auth.can('editPortfolio');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const set = (key: string) => (e: { target: { value: string } }) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));
  const apply = (event: FormEvent) => {
    event.preventDefault();
    void navigate({ to: '/echeances', search: clean(draft) as InstallmentSearch });
  };
  const reset = () => {
    setDraft({});
    void navigate({ to: '/echeances', search: {} });
  };

  return (
    <>
      <PageHeader title={t.installments.title} />
      <Card className="mb-4 p-4">
        <form
          onSubmit={apply}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
          aria-label={t.common.filter}
        >
          <Input
            aria-label={t.installments.policy}
            placeholder={t.installments.policy}
            value={draft.policyNumber ?? ''}
            onChange={set('policyNumber')}
          />
          <Select
            aria-label={t.installments.status}
            value={draft.status ?? ''}
            onChange={set('status')}
          >
            <option value="">
              {t.installments.status} : {t.common.all}
            </option>
            {INSTALLMENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t.status[s]}
              </option>
            ))}
          </Select>
          <div className="flex items-center gap-2">
            <Label className="shrink-0 text-xs">{t.common.from}</Label>
            <Input
              type="date"
              aria-label={`${t.installments.dueDate} ${t.common.from}`}
              value={draft.dueFrom ?? ''}
              onChange={set('dueFrom')}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="shrink-0 text-xs">{t.common.to}</Label>
            <Input
              type="date"
              aria-label={`${t.installments.dueDate} ${t.common.to}`}
              value={draft.dueTo ?? ''}
              onChange={set('dueTo')}
            />
          </div>
          <Input
            aria-label={t.installments.minAmount}
            placeholder={t.installments.minAmount}
            inputMode="decimal"
            value={draft.minAmount ?? ''}
            onChange={set('minAmount')}
          />
          <Input
            aria-label={t.installments.maxAmount}
            placeholder={t.installments.maxAmount}
            inputMode="decimal"
            value={draft.maxAmount ?? ''}
            onChange={set('maxAmount')}
          />
          <Select
            aria-label={t.installments.insurer}
            value={draft.insurerId ?? ''}
            onChange={set('insurerId')}
          >
            <option value="">
              {t.installments.insurer} : {t.common.all}
            </option>
            {refs.insurers.map((i) => (
              <option key={i.id} value={i.id}>
                {i.displayName}
              </option>
            ))}
          </Select>
          <Select
            aria-label={t.installments.product}
            value={draft.productLineId ?? ''}
            onChange={set('productLineId')}
          >
            <option value="">
              {t.installments.product} : {t.common.all}
            </option>
            {refs.productLines.map((p) => (
              <option key={p.id} value={p.id}>
                {p.labelFr}
              </option>
            ))}
          </Select>
          <Select
            aria-label={t.installments.agent}
            value={draft.agentId ?? ''}
            onChange={set('agentId')}
          >
            <option value="">
              {t.installments.agent} : {t.common.all}
            </option>
            {refs.agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label={t.installments.channel}
            value={draft.preferredChannel ?? ''}
            onChange={set('preferredChannel')}
          >
            <option value="">
              {t.installments.channel} : {t.common.all}
            </option>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {t.channels[c]}
              </option>
            ))}
          </Select>
          <Select
            aria-label={t.installments.sequenceStatus}
            value={draft.sequenceStatus ?? ''}
            onChange={set('sequenceStatus')}
          >
            <option value="">
              {t.installments.sequenceStatus} : {t.common.all}
            </option>
            {SEQUENCE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t.sequenceStatus[s]}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={draft.noPhone === 'true'}
              onChange={(e) =>
                setDraft((d) => ({ ...d, noPhone: e.target.checked ? 'true' : undefined }))
              }
            />
            {t.installments.noPhone}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={draft.highRisk === 'true'}
              onChange={(e) =>
                setDraft((d) => ({ ...d, highRisk: e.target.checked ? 'true' : undefined }))
              }
            />
            {t.installments.highRisk}
          </label>
          <div className="flex gap-2 lg:col-span-4">
            <Button type="submit">{t.common.filter}</Button>
            <Button variant="ghost" onClick={reset}>
              {t.common.reset}
            </Button>
          </div>
        </form>
      </Card>
      <Card>
        {canBulk && selected.size > 0 ? (
          <BulkBar
            ids={[...selected]}
            onDone={() => {
              setSelected(new Set());
              void list.refetch();
            }}
          />
        ) : null}
        {list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.isPending ? (
          <Loading />
        ) : list.items.length === 0 ? (
          <Empty />
        ) : (
          <Table>
            <THead>
              <tr>
                {canBulk ? (
                  <TH>
                    <Checkbox
                      aria-label={t.bulk.selectAll}
                      checked={selected.size > 0 && selected.size === list.items.length}
                      onChange={(e) =>
                        setSelected(
                          e.target.checked ? new Set(list.items.map((r) => r.id)) : new Set(),
                        )
                      }
                    />
                  </TH>
                ) : null}
                <TH>{t.installments.policy}</TH>
                <TH>{t.installments.policyholder}</TH>
                <TH>{t.installments.product}</TH>
                <TH>{t.installments.dueDate}</TH>
                <TH className="text-end">{t.installments.amountDue}</TH>
                <TH className="text-end">{t.installments.amountPaid}</TH>
                <TH>{t.installments.status}</TH>
                <TH>{t.installments.risk}</TH>
                <TH>{t.installments.sequence}</TH>
                <TH>{t.installments.agent}</TH>
              </tr>
            </THead>
            <TBody>
              {list.items.map((row) => (
                <TR key={row.id}>
                  {canBulk ? (
                    <TD>
                      <Checkbox
                        aria-label={`${t.bulk.select} ${row.contract.policyNumber}`}
                        checked={selected.has(row.id)}
                        onChange={() => toggle(row.id)}
                      />
                    </TD>
                  ) : null}
                  <TD>
                    <Link
                      to="/echeances/$id"
                      params={{ id: row.id }}
                      className="font-medium whitespace-nowrap text-primary hover:underline"
                    >
                      {row.contract.policyNumber}
                    </Link>
                  </TD>
                  <TD>{row.policyholder.displayName}</TD>
                  <TD>{row.contract.productLabel ?? ''}</TD>
                  <TD className="whitespace-nowrap">{day(row.dueDate, language)}</TD>
                  <TD className="text-end whitespace-nowrap tabular-nums">
                    {money(row.amountDue, language)}
                  </TD>
                  <TD className="text-end whitespace-nowrap tabular-nums">
                    {money(row.amountPaid, language)}
                  </TD>
                  <TD>
                    <InstallmentStatusBadge status={row.status} />
                  </TD>
                  <TD>
                    {row.risk ? (
                      <Badge
                        tone={
                          row.risk.score >= HIGH_RISK
                            ? 'danger'
                            : row.risk.score >= 30
                              ? 'warning'
                              : 'neutral'
                        }
                        title={row.risk.factors
                          .map(
                            (f) =>
                              t.installments.riskFactors[
                                f as keyof typeof t.installments.riskFactors
                              ] ?? f,
                          )
                          .join(', ')}
                      >
                        {row.risk.score}
                      </Badge>
                    ) : (
                      '-'
                    )}
                  </TD>
                  <TD>
                    {row.sequence ? (
                      <div className="flex flex-col gap-1">
                        <SequenceStatusBadge status={row.sequence.status} />
                        <span className="text-xs text-muted-foreground">
                          {t.installments.step} {row.sequence.currentStepNo}
                          {row.sequence.nextRunAt
                            ? ` · ${instant(row.sequence.nextRunAt, tz, language)}`
                            : ''}
                        </span>
                      </div>
                    ) : (
                      '-'
                    )}
                  </TD>
                  <TD>{row.contract.agentName ?? ''}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
        {list.hasNextPage ? (
          <div className="border-t border-border p-3 text-center">
            <Button
              variant="outline"
              disabled={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              {t.common.loadMore}
            </Button>
          </div>
        ) : null}
      </Card>
    </>
  );
}
